import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { endOfWeek, format, startOfWeek, subDays, subWeeks } from 'date-fns';
import { reportService } from '../services/reportService';
import ReportExportMenu from '../components/ReportExportMenu';
import { formatDateOnly } from '../utils/dateUtils';

const ymd = (d) => format(d, 'yyyy-MM-dd');
const WEEK = { weekStartsOn: 0 };
const h = (v) => (Number(v) || 0).toFixed(2);
const pct = (v) => (v == null ? '—' : `${v}%`);

const COLORS = { self: '#2e7d32', manager: '#ed6c02', general: '#bdbdbd' };

const PRESETS = [
  { label: 'This Week', range: () => [startOfWeek(new Date(), WEEK), endOfWeek(new Date(), WEEK)] },
  { label: 'Last Week', range: () => [startOfWeek(subWeeks(new Date(), 1), WEEK), endOfWeek(subWeeks(new Date(), 1), WEEK)] },
  { label: 'Last 4 Weeks', range: () => [startOfWeek(subWeeks(new Date(), 3), WEEK), new Date()] },
  { label: 'Last 30 Days', range: () => [subDays(new Date(), 29), new Date()] },
  { label: 'Since Launch', range: () => [null, null] },
];

function SplitBar({ row }) {
  const total = row.paid_hours || 0;
  if (!total) return <Box sx={{ height: 10, bgcolor: 'grey.100', borderRadius: 1 }} />;
  const parts = [
    { key: 'self', v: row.self_hours, label: 'Picked at kiosk' },
    { key: 'manager', v: row.manager_hours, label: 'Manager allocated' },
    { key: 'general', v: row.general_hours, label: 'General Warehouse' },
  ];
  return (
    <Tooltip
      title={parts.map((p) => `${p.label}: ${h(p.v)} h (${Math.round((p.v / total) * 100)}%)`).join(' · ')}
    >
      <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', minWidth: 120 }}>
        {parts.map((p) =>
          p.v > 0 ? <Box key={p.key} sx={{ width: `${(p.v / total) * 100}%`, bgcolor: COLORS[p.key] }} /> : null
        )}
      </Box>
    </Tooltip>
  );
}

function Legend() {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {[
        ['self', 'Picked at kiosk'],
        ['manager', 'Manager allocated'],
        ['general', 'General Warehouse'],
      ].map(([k, label]) => (
        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: COLORS[k] }} />
          <Typography variant="caption">{label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function Stat({ title, value, caption }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 170px', minWidth: 170 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
      {caption ? <Typography variant="caption" color="text.secondary">{caption}</Typography> : null}
    </Paper>
  );
}

const EMP_COLUMNS = [
  { key: 'name', label: 'Employee' },
  { key: 'punches', label: 'Punches', numeric: true },
  { key: 'paid_hours', label: 'Paid h', numeric: true },
  { key: 'self_hours', label: 'Picked h', numeric: true },
  { key: 'manager_hours', label: 'Manager h', numeric: true },
  { key: 'general_hours', label: 'General h', numeric: true },
  { key: 'pct_on_task', label: '% on a task', numeric: true },
  { key: 'pct_self_of_tagged', label: '% self-picked', numeric: true },
  { key: 'picked_at_clock_in', label: 'Picked at clock-in', numeric: true },
  { key: 'switches', label: 'Switches', numeric: true },
  { key: 'adjusted_punches', label: 'Punches adjusted', numeric: true },
];

const TaskAdoptionReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('from') ?? ymd(startOfWeek(subWeeks(new Date(), 3), WEEK)));
  const [dateTo, setDateTo] = useState(() => searchParams.get('to') ?? ymd(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('pct_on_task');
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportService.getTaskAdoption({ date_from: dateFrom || undefined, date_to: dateTo || undefined });
      setData(res.data);
      setSearchParams({ from: dateFrom || '', to: dateTo || '' }, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, setSearchParams]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    const [from, to] = p.range();
    setDateFrom(from ? ymd(from) : '');
    setDateTo(to ? ymd(to) : '');
  };

  const employees = useMemo(() => {
    const list = [...(data?.employees || [])];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === 'string' || typeof vb === 'string') return dir * String(va ?? '').localeCompare(String(vb ?? ''));
      return dir * ((va ?? -1) - (vb ?? -1));
    });
    return list;
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    setSortDir((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const getExportData = useCallback(() => {
    if (!employees.length) return null;
    const headers = EMP_COLUMNS.map((c) => c.label);
    const rows = employees.map((e) => [
      e.name, String(e.punches), h(e.paid_hours), h(e.self_hours), h(e.manager_hours), h(e.general_hours),
      pct(e.pct_on_task), pct(e.pct_self_of_tagged), `${e.picked_at_clock_in} / ${e.punches}`, String(e.switches),
      String(e.adjusted_punches),
    ]);
    const t = data.totals;
    const footer = [
      'Totals', String(t.punches), h(t.paid_hours), h(t.self_hours), h(t.manager_hours), h(t.general_hours),
      pct(t.pct_on_task), pct(t.pct_self_of_tagged), `${t.picked_at_clock_in} / ${t.punches}`, String(t.switches),
      String(t.adjusted_punches),
    ];
    const range = data.date_from || data.date_to ? `${data.date_from || 'launch'} – ${data.date_to || 'today'}` : 'Since launch';
    return {
      headers,
      rows,
      footer,
      pdfTitle: `Shop Task Adoption · ${range}`,
      filenameBase: `task-adoption_${data.date_from || 'launch'}_${data.date_to || 'today'}`,
      pdfLandscape: true,
    };
  }, [employees, data]);

  const t = data?.totals;

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 0.5 }}>
        Task Adoption
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        How paid hours are getting onto Shop Tasks: picked by the employee at the kiosk, allocated afterwards by a
        manager, or left on General Warehouse. Kiosk hours a manager later edits count as manager allocated.
        {data?.tracking_started_at ? ` Tracking started ${formatDateOnly(data.tracking_started_at)}.` : ''}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Date presets:</Typography>
          {PRESETS.map((p) => (
            <Chip key={p.label} label={p.label} size="small" onClick={() => applyPreset(p)} sx={{ cursor: 'pointer' }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="Date From" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <TextField size="small" label="Date To" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <Button variant="contained" size="small" startIcon={<Search />} onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Run report'}
          </Button>
          <ReportExportMenu disabled={loading || !employees.length} getData={getExportData} defaultFilenameBase="task-adoption" />
        </Box>
      </Paper>

      {loading && !data ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : t ? (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Stat title="Paid hours" value={`${h(t.paid_hours)} h`} caption={`${t.punches} punches`} />
            <Stat title="On a task" value={pct(t.pct_on_task)} caption={`${h(t.self_hours + t.manager_hours)} h picked or allocated`} />
            <Stat title="Self-picked" value={pct(t.pct_self_of_tagged)} caption={`${h(t.self_hours)} h of tasked hours picked at kiosk`} />
            <Stat title="Manager allocated" value={`${h(t.manager_hours)} h`} caption={`${t.adjusted_punches} punches adjusted`} />
            <Stat
              title="Picked at clock-in"
              value={`${t.picked_at_clock_in} / ${t.punches}`}
              caption={`${t.switches} mid-shift task switches`}
            />
          </Box>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Week by week</Typography>
              <Legend />
            </Box>
            {data.weeks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No closed punches in this range yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Week of</TableCell>
                    <TableCell sx={{ width: '40%' }}>Split</TableCell>
                    <TableCell align="right">Paid h</TableCell>
                    <TableCell align="right">% on a task</TableCell>
                    <TableCell align="right">% self-picked</TableCell>
                    <TableCell align="right">Switches</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.weeks.map((w) => (
                    <TableRow key={w.week_start}>
                      <TableCell>{formatDateOnly(w.week_start)}</TableCell>
                      <TableCell><SplitBar row={w} /></TableCell>
                      <TableCell align="right">{h(w.paid_hours)}</TableCell>
                      <TableCell align="right">{pct(w.pct_on_task)}</TableCell>
                      <TableCell align="right">{pct(w.pct_self_of_tagged)}</TableCell>
                      <TableCell align="right">{w.switches}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>By employee</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {EMP_COLUMNS.slice(0, 1).map((c) => (
                      <TableCell key={c.key}>
                        <TableSortLabel active={sortKey === c.key} direction={sortDir} onClick={() => handleSort(c.key)}>{c.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sx={{ minWidth: 140 }}>Split</TableCell>
                    {EMP_COLUMNS.slice(1).map((c) => (
                      <TableCell key={c.key} align="right">
                        <TableSortLabel active={sortKey === c.key} direction={sortDir} onClick={() => handleSort(c.key)}>{c.label}</TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={EMP_COLUMNS.length + 1} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No closed punches in this range yet.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((e) => (
                      <TableRow key={e.employee_id} hover>
                        <TableCell>{e.name}</TableCell>
                        <TableCell><SplitBar row={e} /></TableCell>
                        <TableCell align="right">{e.punches}</TableCell>
                        <TableCell align="right">{h(e.paid_hours)}</TableCell>
                        <TableCell align="right">{h(e.self_hours)}</TableCell>
                        <TableCell align="right">{h(e.manager_hours)}</TableCell>
                        <TableCell align="right">{h(e.general_hours)}</TableCell>
                        <TableCell align="right">{pct(e.pct_on_task)}</TableCell>
                        <TableCell align="right">{pct(e.pct_self_of_tagged)}</TableCell>
                        <TableCell align="right">{e.picked_at_clock_in} / {e.punches}</TableCell>
                        <TableCell align="right">{e.switches}</TableCell>
                        <TableCell align="right">{e.adjusted_punches}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : null}
    </Box>
  );
};

export default TaskAdoptionReport;
